import React, { useEffect, useState } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';

export default function WIMFineTiers() {
  const [fineTiers, setFineTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('all'); // all | active | inactive
  const [form, setForm] = useState({
    name: '', vehicle_type: 'truck', axle_min: '', axle_max: '',
    excess_min_kg: '', excess_max_kg: '', calculation: 'flat', fine_amount: '',
    road_damage_kip: '', weighing_service_kip: '', management_per_km_kip: '', per_trip_fine_kip: '',
    active: true
  });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const res = await API.get('/wim/fine-tiers');
      setFineTiers(res.data);
    } catch (e) {
      toast.error('โหลดตารางค่าปรับไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    // normalize numeric fields
    const floatFields = ['excess_min_kg','excess_max_kg','fine_amount','road_damage_kip','weighing_service_kip','management_per_km_kip','per_trip_fine_kip'];
    const intFields = ['axle_min','axle_max'];
    [...floatFields, ...intFields].forEach(k => { if (payload[k] === '') payload[k] = null; });
    floatFields.forEach(k => { if (payload[k] != null) payload[k] = parseFloat(String(payload[k]).toString().replace(/,/g,'')); });
    intFields.forEach(k => { if (payload[k] != null) payload[k] = parseInt(String(payload[k]).toString().replace(/,/g,'')); });

    try {
      if (editingId) {
        await API.put(`/wim/fine-tiers/${editingId}`, payload);
        toast.success('ອັບເດດສໍາເລັດ');
      } else {
        await API.post('/wim/fine-tiers', payload);
        toast.success('ບັນທຶກສໍາເລັດ');
      }
      setForm({ name: '', vehicle_type: 'truck', axle_min: '', axle_max: '', excess_min_kg: '', excess_max_kg: '', calculation: 'flat', fine_amount: '', road_damage_kip: '', weighing_service_kip: '', management_per_km_kip: '', per_trip_fine_kip: '', active: true });
      setEditingId(null);
      load();
    } catch (e) {
      toast.error('ບັນທຶກ/ອັບເດດລົ້ມເຫຼວ');
    }
  };

  const toggleActive = async (tier) => {
    try {
      await API.put(`/wim/fine-tiers/${tier.id}`, { ...tier, active: !tier.active });
      load();
    } catch {
      toast.error('เปลี่ยนสถานะไม่สำเร็จ');
    }
  };

  const removeTier = async (tier) => {
    if (!window.confirm('ลบรายการนี้หรือไม่?')) return;
    try {
      await API.delete(`/wim/fine-tiers/${tier.id}`);
      load();
    } catch {
      toast.error('ลบไม่สำเร็จ');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Filtered list
  const filtered = fineTiers.filter(t => view === 'all' ? true : view === 'active' ? !!t.active : !t.active);

  // Helpers
  const fmtLAK = n => `${Number(n||0).toLocaleString()} LAK`;
  const fmtRangeTon = (minKg, maxKg) => {
    const minT = minKg != null ? (Number(minKg)/1000).toFixed(1) : '-';
    const maxT = maxKg != null ? (Number(maxKg)/1000).toFixed(1) : 'ຂື້ນໄປ';
    return `${minT} - ${maxT} ໂຕນ`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">🧮 ຕາຕະລາງອັດຕາຄ່າປັບ (fine_tiers)</h1>
        <div className="flex gap-2">
          <button onClick={()=>setView('all')} className={`px-4 py-2 rounded-lg border ${view==='all'?'bg-blue-600 text-white border-blue-600':'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}>ທັງໝົດ</button>
          <button onClick={()=>setView('active')} className={`px-4 py-2 rounded-lg border ${view==='active'?'bg-green-600 text-white border-green-600':'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}>ໃຊ້ງານ</button>
          <button onClick={()=>setView('inactive')} className={`px-4 py-2 rounded-lg border ${view==='inactive'?'bg-amber-600 text-white border-amber-600':'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}>ປິດ</button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold mb-3">ເພີ່ມ/ແກ້ໄຂອັດຕາ</h2>
          <button onClick={async()=>{ try{ await API.post('/wim/fine-tiers/reset-and-seed-lao'); toast.success('ຕັ້ງຄ່າຕາຕະລາງລາວ (ລ້າງ+ເພີ່ມ) ສໍາເລັດ'); load(); } catch{ toast.error('ລົ້ມເຫຼວ'); } }} className="bg-indigo-600 hover:bg-indigo-700 text-white py-1 px-3 rounded">ຕັ້ງຄ່າຕາຕະລາງລາວ</button>
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="p-2 border rounded" placeholder="ຊື່" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
          <input className="p-2 border rounded" placeholder="ປະເພດລົດ (ປ່ອຍວ່າງ = ທັງໝົດ)" value={form.vehicle_type} onChange={e=>setForm({...form,vehicle_type:e.target.value})} />
          <div className="flex gap-2">
            <input className="p-2 border rounded w-full" placeholder="ເພົາ ຕໍ່ໍາ (min)" value={form.axle_min} onChange={e=>setForm({...form,axle_min:e.target.value})} />
            <input className="p-2 border rounded w-full" placeholder="ເພົາ ສູງ (max)" value={form.axle_max} onChange={e=>setForm({...form,axle_max:e.target.value})} />
          </div>
          <div className="flex gap-2">
            <input className="p-2 border rounded w-full" placeholder="ນ້ໍາໜັກເກີນ (ກກ.) min" value={form.excess_min_kg} onChange={e=>setForm({...form,excess_min_kg:e.target.value})} />
            <input className="p-2 border rounded w-full" placeholder="ນ້ໍາໜັກເກີນ (ກກ.) max" value={form.excess_max_kg} onChange={e=>setForm({...form,excess_max_kg:e.target.value})} />
          </div>
          <select className="p-2 border rounded" value={form.calculation} onChange={e=>setForm({...form,calculation:e.target.value})}>
            <option value="flat">ຄ່າຄົງທີ່</option>
            <option value="per_kg">ຕໍ່ກກ.</option>
          </select>
          <input className="p-2 border rounded" placeholder="ຄ່າປັບຫຼັກ (LAK ຫຼື LAK/ກກ.)" value={form.fine_amount} onChange={e=>setForm({...form,fine_amount:e.target.value})} />
          <input className="p-2 border rounded" placeholder="ການເສຍຫາຍເສັ້ນທາງ (LAK)" value={form.road_damage_kip} onChange={e=>setForm({...form,road_damage_kip:e.target.value})} />
          <input className="p-2 border rounded" placeholder="ຄ່າບໍລິການຊັ່ງ (LAK)" value={form.weighing_service_kip} onChange={e=>setForm({...form,weighing_service_kip:e.target.value})} />
          <input className="p-2 border rounded" placeholder="ຄ່າຄຸ້ມຄອງ/1 ກມ (LAK)" value={form.management_per_km_kip} onChange={e=>setForm({...form,management_per_km_kip:e.target.value})} />
          <input className="p-2 border rounded" placeholder="ຄ່າປັບ/ຖ້ຽວ/ຄັນ (LAK)" value={form.per_trip_fine_kip} onChange={e=>setForm({...form,per_trip_fine_kip:e.target.value})} />
          <div className="flex items-center gap-2">
            <button type="submit" className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded">{editingId ? 'ອັບເດດ' : 'ບັນທຶກ'}</button>
            {editingId && (
              <button type="button" onClick={()=>{ setEditingId(null); setForm({ name: '', vehicle_type: 'truck', axle_min: '', axle_max: '', excess_min_kg: '', excess_max_kg: '', calculation: 'flat', fine_amount: '', road_damage_kip: '', weighing_service_kip: '', management_per_km_kip: '', per_trip_fine_kip: '', active: true }); }} className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded">ຍົກເລີກ</button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">ລາຍການກົດ</h2>
          <button onClick={load} className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded">ໂຫຼດໃໝ່</button>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="p-3">ນ້ໍາໜັກເກີນ (ໂຕນ)</th>
                <th className="p-3">ປະເພດລົດ</th>
                <th className="p-3">ວິທີຄິດ</th>
                <th className="p-3">ຄ່າປັບ</th>
                <th className="p-3">ການເສຍຫາຍເສັ້ນທາງ</th>
                <th className="p-3">ຄ່າບໍລິການຊັ່ງ</th>
                <th className="p-3">ຄ່າຄຸ້ມຄອງ/1 ກມ</th>
                <th className="p-3">ຄ່າປັບ/ຖ້ຽວ/ຄັນ</th>
                <th className="p-3">ສະຖານະ</th>
                <th className="p-3">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, idx) => (
                <tr key={`tier-${t.id}-${idx}`} className="border-b hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="p-3 font-medium text-gray-800 dark:text-white">{fmtRangeTon(t.excess_min_kg, t.excess_max_kg)}</td>
                  <td className="p-3 text-gray-700 dark:text-gray-200">{t.vehicle_type || 'ທັງໝົດ'}</td>
                  <td className="p-3">{t.calculation === 'per_kg' ? 'ຕໍ່ກກ.' : 'ຄ່າຄົງທີ່'}</td>
                  <td className="p-3 font-semibold">{fmtLAK(t.fine_amount)}</td>
                  <td className="p-3">{fmtLAK(t.road_damage_kip)}</td>
                  <td className="p-3">{fmtLAK(t.weighing_service_kip)}</td>
                  <td className="p-3">{fmtLAK(t.management_per_km_kip)}</td>
                  <td className="p-3">{fmtLAK(t.per_trip_fine_kip)}</td>
                  <td className="p-3">
                    {t.active ? (
                      <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">ໃຊ້ງານ</span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">ປິດ</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingId(t.id); setForm({
                        name: t.name || '', vehicle_type: t.vehicle_type || 'truck', axle_min: t.axle_min ?? '', axle_max: t.axle_max ?? '',
                        excess_min_kg: t.excess_min_kg ?? '', excess_max_kg: t.excess_max_kg ?? '', calculation: t.calculation || 'flat', fine_amount: t.fine_amount ?? '',
                        road_damage_kip: t.road_damage_kip ?? '', weighing_service_kip: t.weighing_service_kip ?? '', management_per_km_kip: t.management_per_km_kip ?? '', per_trip_fine_kip: t.per_trip_fine_kip ?? '',
                        active: !!t.active
                      }); }} className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white">ແກ້ໄຂ</button>
                      <button onClick={() => toggleActive(t)} className={`px-3 py-1 rounded ${t.active?'bg-amber-500 hover:bg-amber-600':'bg-green-600 hover:bg-green-700'} text-white`}>{t.active?'ປິດໃຊ້':'ເປີດໃຊ້'}</button>
                      <button onClick={() => removeTier(t)} className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white">ລົບ</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-6 text-center text-gray-500">ບໍ່ມີຂໍ້ມູນ</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
